import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider
} from '@mui/material';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

const QuestionnaireForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [showFollowUp, setShowFollowUp] = useState({});

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/questionnaires/token/${token}`);
        if (response.data.success) {
          setQuestionnaire(response.data.data.questionnaire);
          setQuestions(response.data.data.questions);
        } else {
          throw new Error(response.data.message || 'Failed to fetch questionnaire');
        }
        
        // Initialize form values
        const initialValues = {};
        // Add respondent_email field
        initialValues['respondent_email'] = '';
        
        // Make sure we're using the questions from the correct part of the response
        const questionsList = response.data.data.questions || [];
        questionsList.forEach(question => {
          if (question.type === 'checkbox') {
            initialValues[`question_${question.id}`] = [];
          } else if (question.type === 'file') {
            initialValues[`question_${question.id}`] = null;
          } else {
            initialValues[`question_${question.id}`] = '';
          }
        });
        
        setFormValues(initialValues);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching questionnaire:', err);
        setError('Failed to load the questionnaire. Please try again later.');
        setLoading(false);
      }
    };
    
    if (token) {
      fetchQuestionnaire();
    }
  }, [token]);

  // Group questions by category
  const getQuestionsByCategory = () => {
    const categories = {};
    
    questions.forEach(question => {
      const categoryId = question.category_id;
      if (!categories[categoryId]) {
        categories[categoryId] = [];
      }
      categories[categoryId].push(question);
    });
    
    return categories;
  };

  // Handle conditional follow-up questions
  const handleValueChange = (question, value, setFieldValue) => {
    if (question.has_follow_up && question.follow_up_question_id) {
      let showFollowUpQuestion = false;
      
      switch (question.follow_up_condition) {
        case 'equals':
          showFollowUpQuestion = value === question.follow_up_value;
          break;
        case 'not_equals':
          showFollowUpQuestion = value !== question.follow_up_value;
          break;
        case 'contains':
          if (Array.isArray(value)) {
            showFollowUpQuestion = value.includes(question.follow_up_value);
          } else {
            showFollowUpQuestion = value.includes(question.follow_up_value);
          }
          break;
        case 'not_contains':
          if (Array.isArray(value)) {
            showFollowUpQuestion = !value.includes(question.follow_up_value);
          } else {
            showFollowUpQuestion = !value.includes(question.follow_up_value);
          }
          break;
        default:
          break;
      }
      
      setShowFollowUp({
        ...showFollowUp,
        [question.follow_up_question_id]: showFollowUpQuestion
      });
      
      // Reset the follow-up question value if it's hidden
      if (!showFollowUpQuestion) {
        const followUpQuestion = questions.find(q => q.id === question.follow_up_question_id);
        if (followUpQuestion) {
          const fieldName = `question_${followUpQuestion.id}`;
          if (followUpQuestion.type === 'checkbox') {
            setFieldValue(fieldName, []);
          } else if (followUpQuestion.type === 'file') {
            setFieldValue(fieldName, null);
          } else {
            setFieldValue(fieldName, '');
          }
        }
      }
    }
  };

  // Create validation schema based on question requirements
  const createValidationSchema = () => {
    const schema = {};
    
    // Add email validation
    schema.respondent_email = Yup.string()
      .email('Invalid email address')
      .required('Email is required');
    
    // Add validation for each question
    questions.forEach(question => {
      const fieldName = `question_${question.id}`;
      
      // Skip validation for hidden follow-up questions
      if (question.follow_up_question_id && !showFollowUp[question.id]) {
        return;
      }
      
      switch (question.type) {
        case 'short_text':
        case 'long_text':
          schema[fieldName] = question.required
            ? Yup.string().required('This field is required')
            : Yup.string();
          break;
        case 'radio':
        case 'dropdown':
          schema[fieldName] = question.required
            ? Yup.string().required('This field is required')
            : Yup.string();
          break;
        case 'checkbox':
          schema[fieldName] = question.required
            ? Yup.array().min(1, 'At least one option must be selected')
            : Yup.array();
          break;
        case 'file':
          schema[fieldName] = question.required
            ? Yup.mixed().required('A file is required')
            : Yup.mixed();
          break;
        default:
          break;
      }
    });
    
    return Yup.object().shape(schema);
  };

  const validationSchema = createValidationSchema();

  const handleNext = (validateForm, errors, touched, setTouched) => {
    // Get questions for the current step
    const currentCategoryId = categories[activeStep];
    const currentQuestions = questionsByCategory[currentCategoryId] || [];
    
    // Create an object to track which fields need to be touched
    const fieldsToTouch = {};
    
    // Add all fields from the current step to the touch tracker
    currentQuestions.forEach(question => {
      // Skip follow-up questions that should not be shown
      if (question.follow_up_question_id && !showFollowUp[question.id]) {
        return;
      }
      
      const fieldName = `question_${question.id}`;
      fieldsToTouch[fieldName] = true;
    });
    
    // If this is the first step, also touch the email field
    if (activeStep === 0) {
      fieldsToTouch['respondent_email'] = true;
    }
    
    // Mark all fields as touched to trigger validation
    setTouched({ ...touched, ...fieldsToTouch });
    
    // Validate the form
    validateForm().then(validationErrors => {
      // Check if there are any errors for the current fields
      const hasErrors = Object.keys(fieldsToTouch).some(field => validationErrors[field]);
      
      // Only proceed if there are no errors
      if (!hasErrors) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    });
  };

  const handleSubmit = (values) => {
    // Prepare the form data for submission
    const formData = new FormData();
    formData.append('questionnaire_id', questionnaire.id);
    formData.append('respondent_email', values.respondent_email || 'anonymous@example.com');
    
    // Add all question answers to the form data
    Object.keys(values).forEach(key => {
      if (key.startsWith('question_')) {
        const questionId = key.split('_')[1];
        const question = questions.find(q => q.id === parseInt(questionId));
        
        if (question) {
          if (question.type === 'file' && values[key]) {
            formData.append(key, values[key]);
          } else if (question.type === 'checkbox') {
            formData.append(key, JSON.stringify(values[key]));
          } else {
            formData.append(key, values[key]);
          }
        }
      }
    });
    
    // Set loading state while submitting
    setLoading(true);
    
    // Submit the form data to the backend API
    axios.post(`${process.env.REACT_APP_API_URL}/questionnaires/${token}/submit`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(response => {
        setLoading(false);
        navigate('/success');
      })
      .catch(error => {
        console.error('Error submitting form:', error);
        setError('Failed to submit the form. Please try again.');
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!questionnaire) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Questionnaire not found or has expired.</Alert>
      </Box>
    );
  }

  // Get questions grouped by category
  const questionsByCategory = getQuestionsByCategory();
  
  // Get categories for the stepper
  const categories = Object.keys(questionsByCategory);
  
  // Get questions for the current step
  const currentCategoryId = categories[activeStep];
  const currentQuestions = questionsByCategory[currentCategoryId] || [];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {questionnaire.title}
        </Typography>
        {questionnaire.description && (
          <Typography variant="body1" paragraph>
            {questionnaire.description}
          </Typography>
        )}
      </Paper>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {categories.map((categoryId, index) => {
          const category = questionsByCategory[categoryId][0]?.category;
          return (
            <Step key={categoryId}>
              <StepLabel>{category ? category.name : `Section ${index + 1}`}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isValid, validateForm, setTouched }) => (
          <Form>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                {currentQuestions[0]?.category?.name || `Section ${activeStep + 1}`}
              </Typography>
              
              {activeStep === 0 && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    id="respondent_email"
                    name="respondent_email"
                    label="Your Email"
                    value={values.respondent_email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.respondent_email && Boolean(errors.respondent_email)}
                    helperText={touched.respondent_email && errors.respondent_email}
                    margin="normal"
                  />
                </Box>
              )}
              
              {currentQuestions.map(question => {
                // Skip follow-up questions that should not be shown
                if (question.follow_up_question_id && !showFollowUp[question.id]) {
                  return null;
                }
                
                const fieldName = `question_${question.id}`;
                const hasError = touched[fieldName] && Boolean(errors[fieldName]);
                
                return (
                  <Box key={question.id} sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" component="div" gutterBottom>
                      {question.title}
                      {question.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>
                    
                    {question.description && (
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {question.description}
                      </Typography>
                    )}
                    
                    {question.type === 'short_text' && (
                      <TextField
                        fullWidth
                        id={fieldName}
                        name={fieldName}
                        value={values[fieldName]}
                        onChange={(e) => {
                          handleChange(e);
                          handleValueChange(question, e.target.value, setFieldValue);
                        }}
                        onBlur={handleBlur}
                        error={hasError}
                        helperText={hasError && errors[fieldName]}
                      />
                    )}
                    
                    {question.type === 'long_text' && (
                      <TextField
                        fullWidth
                        id={fieldName}
                        name={fieldName}
                        multiline
                        rows={4}
                        value={values[fieldName]}
                        onChange={(e) => {
                          handleChange(e);
                          handleValueChange(question, e.target.value, setFieldValue);
                        }}
                        onBlur={handleBlur}
                        error={hasError}
                        helperText={hasError && errors[fieldName]}
                      />
                    )}
                    
                    {question.type === 'radio' && (
                      <FormControl component="fieldset" error={hasError}>
                        <RadioGroup
                          name={fieldName}
                          value={values[fieldName]}
                          onChange={(e) => {
                            handleChange(e);
                            handleValueChange(question, e.target.value, setFieldValue);
                          }}
                        >
                          {(Array.isArray(question.options) ? question.options : []).map((option, index) => (
                            <FormControlLabel
                              key={index}
                              value={option}
                              control={<Radio />}
                              label={option}
                            />
                          ))}
                        </RadioGroup>
                        {hasError && (
                          <Typography variant="caption" color="error">
                            {errors[fieldName]}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                    
                    {question.type === 'checkbox' && (
                      <FormControl component="fieldset" error={hasError}>
                        <FormGroup>
                          {(Array.isArray(question.options) ? question.options : []).map((option, index) => (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  name={fieldName}
                                  value={option}
                                  checked={values[fieldName]?.includes(option) || false}
                                  onChange={(e) => {
                                    const currentValues = [...(values[fieldName] || [])];
                                    if (e.target.checked) {
                                      currentValues.push(option);
                                    } else {
                                      const idx = currentValues.indexOf(option);
                                      if (idx !== -1) {
                                        currentValues.splice(idx, 1);
                                      }
                                    }
                                    setFieldValue(fieldName, currentValues);
                                    handleValueChange(question, currentValues, setFieldValue);
                                  }}
                                />
                              }
                              label={option}
                            />
                          ))}
                        </FormGroup>
                        {hasError && (
                          <Typography variant="caption" color="error">
                            {errors[fieldName]}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                    
                    {question.type === 'dropdown' && (
                      <FormControl fullWidth error={hasError}>
                        <InputLabel id={`${fieldName}-label`}>{question.title}</InputLabel>
                        <Select
                          labelId={`${fieldName}-label`}
                          id={fieldName}
                          name={fieldName}
                          value={values[fieldName]}
                          onChange={(e) => {
                            handleChange(e);
                            handleValueChange(question, e.target.value, setFieldValue);
                          }}
                          onBlur={handleBlur}
                          label={question.title}
                        >
                          {(Array.isArray(question.options) ? question.options : []).map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {hasError && (
                          <Typography variant="caption" color="error">
                            {errors[fieldName]}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                    
                    {question.type === 'file' && (
                      <Box>
                        <input
                          type="file"
                          id={fieldName}
                          name={fieldName}
                          onChange={(event) => {
                            setFieldValue(fieldName, event.currentTarget.files[0]);
                          }}
                        />
                        {hasError && (
                          <Typography variant="caption" color="error">
                            {errors[fieldName]}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                disabled={activeStep === 0}
                onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)}
              >
                Back
              </Button>
              
              {activeStep < categories.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleNext(validateForm, errors, touched, setTouched)}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid}
                  onClick={() => {
                    // Touch all fields to trigger validation
                    const allFields = {};
                    questions.forEach(q => {
                      allFields[`question_${q.id}`] = true;
                    });
                    allFields['respondent_email'] = true;
                    setTouched({ ...touched, ...allFields });
                  }}
                >
                  Submit
                </Button>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default QuestionnaireForm;
